import { employees } from '@markku/db'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'
import { attachmentResponseSchema, withAttachments } from './attachment'
import { emailSchema } from './common'
import { selectEmployeeSkillWithDetailsSchema } from './skill'

// Share of working time spent on field work, as a numeric string for the
// decimal column. Bounded because the capacity model divides it by 100 and
// multiplies a team's available days by it, unclamped — a stray 250 would
// price one person as 2,5 field heads across the whole johtoryhmä report, and
// a non-numeric string would reach Postgres as a 500 rather than a 400.
const fieldWorkPercentSchema = z
  .string()
  .refine((value) => {
    const percent = Number(value)
    return (
      value.trim() !== '' &&
      Number.isFinite(percent) &&
      percent >= 0 &&
      percent <= 100
    )
  }, 'Kenttätyön osuus on 0–100 %')
  .optional()

export const selectEmployeeSchema = createSelectSchema(employees)

// employees.role is free text; this value marks subcontractors.
export const SUBCONTRACTOR_ROLE = 'alihankkija'

export const insertEmployeeSchema = createInsertSchema(employees).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  userId: true, // Will be auto-created
})

export const updateEmployeeSchema = z.object({
  // Allow updating user fields - requires id for safety
  user: z
    .object({
      id: z.number(),
      firstName: z.string().optional(),
      lastName: z.string().optional(),
      email: emailSchema.optional(),
      phoneNumber: z.string().optional().nullable(),
    })
    .optional(),
  // Employee fields
  role: z.string().optional(),
  employmentStartDate: z.string().optional().nullable(),
  employmentEndDate: z.string().optional().nullable(),
  birthDate: z.string().optional().nullable(),
  gender: z
    .enum(['male', 'female', 'other', 'prefer_not_to_say'])
    .optional()
    .nullable(),
  avatarAttachmentId: z.number().optional().nullable(),
  defaultLatitude: z.string().optional().nullable(),
  defaultLongitude: z.string().optional().nullable(),
  defaultLocationAddress: z.string().optional().nullable(),
  availabilityMode: z.enum(['default', 'explicit']).optional(),
  bookableServicePresets: z.array(z.string()).optional(),
  defaultHelperUserId: z.number().optional().nullable(),
  commissionPercent: z.string().optional().nullable(),
  subcontractorCompanyName: z.string().optional().nullable(),
  subcontractorVatLiable: z.boolean().optional(),
  fieldWorkPercent: fieldWorkPercentSchema,
  teamId: z.number().optional().nullable(),
  nuohousFirstFireplaceMinutes: z.number().int().min(1).optional().nullable(),
  nuohousExtraFireplaceMinutes: z.number().int().min(0).optional().nullable(),
  // Skills relation
  skills: z
    .array(
      z.object({
        skillId: z.number(),
        level: z.enum(['beginner', 'intermediate', 'expert']),
      }),
    )
    .optional(),
})

// Schema for creating employee with user info - nested structure for relation upsert
export const createEmployeeWithUserSchema = z.object({
  // User data is nested
  user: z.object({
    id: z.number().optional(),
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    email: emailSchema.nullable(),
    phoneNumber: z.string().optional().nullable(),
  }),
  // Employee-specific fields
  role: z.string().min(1, 'Role is required'),
  employmentStartDate: z.string().optional().nullable(),
  employmentEndDate: z.string().optional().nullable(),
  birthDate: z.string().optional().nullable(), // Will be date string from form
  gender: z
    .enum(['male', 'female', 'other', 'prefer_not_to_say'])
    .optional()
    .nullable(),
  avatarAttachmentId: z.number().optional().nullable(),
  defaultLatitude: z.string().optional().nullable(),
  defaultLongitude: z.string().optional().nullable(),
  defaultLocationAddress: z.string().optional().nullable(),
  availabilityMode: z.enum(['default', 'explicit']).optional(),
  bookableServicePresets: z.array(z.string()).optional(),
  defaultHelperUserId: z.number().optional().nullable(),
  commissionPercent: z.string().optional().nullable(),
  subcontractorCompanyName: z.string().optional().nullable(),
  subcontractorVatLiable: z.boolean().optional(),
  fieldWorkPercent: fieldWorkPercentSchema,
  teamId: z.number().optional().nullable(),
  nuohousFirstFireplaceMinutes: z.number().int().min(1).optional().nullable(),
  nuohousExtraFireplaceMinutes: z.number().int().min(0).optional().nullable(),
  // Skills relation
  skills: z
    .array(
      z.object({
        skillId: z.number(),
        level: z.enum(['beginner', 'intermediate', 'expert']),
      }),
    )
    .optional(),
})

// Schema for employee with user relation
export const selectEmployeeWithUserSchema = withAttachments(
  selectEmployeeSchema.extend({
    user: z
      .object({
        id: z.number(),
        firstName: z.string(),
        lastName: z.string(),
        email: z.string().nullable(),
        phoneNumber: z.string().nullable(),
        status: z.enum(['active', 'inactive']).optional(),
      })
      .optional(),
    avatar: attachmentResponseSchema.partial().optional().nullable(),
    defaultHelper: z
      .object({
        id: z.number(),
        firstName: z.string(),
        lastName: z.string(),
      })
      .optional()
      .nullable(),
    team: z
      .object({
        id: z.number(),
        name: z.string(),
        bookingStartTime: z.string().nullable(),
        bookingEndTime: z.string().nullable(),
      })
      .optional()
      .nullable(),
    employeeSkills: z.array(selectEmployeeSkillWithDetailsSchema).optional(),
  }),
)

// Type exports
export type Employee = z.infer<typeof selectEmployeeSchema>
export type EmployeeWithUser = z.infer<typeof selectEmployeeWithUserSchema>
export type CreateEmployee = z.infer<typeof insertEmployeeSchema>
export type UpdateEmployee = z.infer<typeof updateEmployeeSchema>
export type CreateEmployeeWithUser = z.infer<
  typeof createEmployeeWithUserSchema
>
